import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export interface PlanLimits {
  plan?: string | null;
  camera_limit?: number | null;
  user_limit?: number | null;
  storage_limit_gb?: number | null;
  retention_max_days?: number | null;
  ai_event_quota_month?: number | null;
  report_schedule_freq?: string | null;
  api_rate_limit_per_min?: number | null;
  video_wall_enabled: boolean;
  export_enabled: boolean;
  ai_modules_enabled: string[];
  grace_period_days: number;
  is_trial: boolean;
  expires_at?: string | null;
  status?: string | null;
}

export interface UsageSnapshot {
  cameras_used: number;
  users_used: number;
  storage_used_gb: number;
  ai_events_month: number;
}

export interface LicenseOverview {
  limits: PlanLimits;
  usage: UsageSnapshot;
  features: string[];
  lifecycle_state: string;
}

export interface Branding {
  tenant_id: string;
  logo_url?: string | null;
  primary_color: string;
  accent_color: string;
  custom_domain?: string | null;
}

const licensingKey = ["licensing"] as const;

export function useMyLicense() {
  return useQuery({
    queryKey: [...licensingKey, "me"],
    queryFn: async () => {
      const { data } = await api.get<LicenseOverview>("/api/v1/licensing/me");
      return data;
    },
  });
}

export function useBranding() {
  return useQuery({
    queryKey: [...licensingKey, "branding"],
    queryFn: async () => {
      const { data } = await api.get<Branding>("/api/v1/licensing/branding");
      return data;
    },
  });
}

export function usePutBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Branding>) => {
      const { data } = await api.put<Branding>("/api/v1/licensing/branding", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: licensingKey }),
  });
}

export interface SuperLicenseUpsert {
  plan: string;
  camera_limit?: number | null;
  user_limit?: number | null;
  storage_limit_gb?: number | null;
  retention_max_days?: number | null;
  ai_event_quota_month?: number | null;
  api_rate_limit_per_min?: number | null;
  video_wall_enabled?: boolean;
  export_enabled?: boolean;
  is_trial?: boolean;
  expires_at?: string | null;
}

export function useSuperUpsertLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { tenantId: string; body: SuperLicenseUpsert }) => {
      const { data } = await api.put<PlanLimits>(
        `/api/v1/licensing/super/${input.tenantId}`,
        input.body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: licensingKey }),
  });
}

/** Lightweight UI feature gate — reads the resolved plan features. */
export function useHasFeature(featureKey: string): boolean {
  const { data } = useMyLicense();
  if (!data) return true; // optimistic until loaded
  if (!data.limits.plan) return true; // no plan → ungated
  return data.features.includes(featureKey);
}
